import { useEffect, useMemo, useState } from 'react';
import CloseIcon from '../assets/close.svg';
import { useTranslation } from 'react-i18next';
import { AiOutlineSearch } from 'react-icons/ai';
import TikmarkIcon from '../assets/tikmark.svg';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import { useFilterConfig } from '../contexts/filters';
import { useAppliedFilters } from '../hooks/useAppliedFilters';
import { slugify } from '../utils/filters';

const MERCHANT_SORT_OPTIONS = [
  { id: 'minOrderAmount', labelKey: 'filterMinOrderAmount' },
  { id: 'avgRating', labelKey: 'filterAverageRating' },
];

const PRODUCT_SORT_OPTIONS = [
  { id: 'priceAsc', labelKey: 'filterPriceLowToHigh' },
  { id: 'priceDesc', labelKey: 'filterPriceHighToLow' },
  { id: 'inStockFirst', labelKey: 'filterInStockFirst' },
];

const FilterModal = ({ isFilterModalOpen, onClose, onApply }) => {
  const { t } = useTranslation();
  const { mode, categories } = useFilterConfig();
  const appliedFilters = useAppliedFilters();
  const [selectedItems, setSelectedItems] = useState(() => new Set(appliedFilters));
  const [categorySearch, setCategorySearch] = useState('');
  const [show, setShow] = useState(false);
  useBodyScrollLock(isFilterModalOpen);

  useEffect(() => {
    if (isFilterModalOpen) {
      setSelectedItems(new Set(appliedFilters));
      setCategorySearch('');
      setTimeout(() => setShow(true), 10);
    } else {
      setShow(false);
    }
  }, [isFilterModalOpen, appliedFilters]);

  useEffect(() => {
    if (!isFilterModalOpen) {
      setSelectedItems(new Set(appliedFilters));
    }
  }, [appliedFilters, isFilterModalOpen]);

  const effectiveMode = mode || 'merchants';
  const sortOptions = effectiveMode === 'products'
    ? PRODUCT_SORT_OPTIONS
    : MERCHANT_SORT_OPTIONS;

  const categoryChips = useMemo(() => {
    const seen = new Map();
    (categories || []).forEach((raw) => {
      const label = typeof raw === 'string' ? raw.trim() : '';
      if (!label) {
        return;
      }
      const id = `cat:${slugify(label)}`;
      if (!seen.has(id)) {
        seen.set(id, { id, label });
      }
    });
    return [...seen.values()];
  }, [categories]);

  const visibleCategoryChips = useMemo(() => {
    const needle = categorySearch.trim().toLowerCase();
    if (!needle) {
      return categoryChips;
    }
    return categoryChips.filter((c) => c.label.toLowerCase().includes(needle));
  }, [categoryChips, categorySearch]);

  const handleItemClick = (item) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(item)) {
        next.delete(item);
      } else {
        next.add(item);
      }
      return next;
    });
  };

  const handleSortClick = (sortId) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      sortOptions.forEach((opt) => {
        if (opt.id !== sortId) {
          next.delete(opt.id);
        }
      });
      if (next.has(sortId)) {
        next.delete(sortId);
      } else {
        next.add(sortId);
      }
      return next;
    });
  };

  return (
    <div>
      <div
        className={`fixed bottom-0 lg:top-0 left-0 w-full lg:w-[550px] h-fit lg:h-full max-h-[85dvh] lg:max-h-full overflow-y-auto bg-white z-50 transition-transform duration-300 ${show ? 'modalanimated' : 'whennotmodalclose'}`}
      >
        <div className='flex justify-between items-center w-full p-4 border-b border-black border-opacity-10'>
          <h2 className='font-semibold text-xl text-[#000000de]'>{t('sortfilters')}</h2>
          <button
            className='w-9 h-9 flex items-center justify-center rounded-full transition-colors duration-300 hover:bg-[#e7e6e6bf]'
            onClick={onClose}
          >
            <img src={CloseIcon} alt="CloseIcon" />
          </button>
        </div>

        <div className='p-4 border-b border-black border-opacity-10'>
          <div>
            <h2 className='text-sm font-semibold'>{t('sortby')}</h2>
            <ul className='flex flex-wrap gap-[10px] mt-3 mb-5 text-[#000000DE]'>
              {sortOptions.map((opt) => (
                <li
                  key={opt.id}
                  className={`px-3 w-fit h-8 flex items-center text-sm font-medium rounded cursor-pointer transition-colors duration-300 ${selectedItems.has(opt.id) ? 'bg-black text-white' : 'bg-[#f3f3f4] hover:bg-[#e0e0e0]'}`}
                  onClick={() => handleSortClick(opt.id)}
                >
                  {t(opt.labelKey)}
                </li>
              ))}
            </ul>
          </div>

          {categoryChips.length > 0 && (
            <div>
              <h2 className='text-sm font-semibold'>
                {effectiveMode === 'products' ? t('category') : t('cuisine')}
              </h2>
              <div className='bg-[#f3f3f4] flex items-center gap-2 px-3 h-10 rounded w-full mt-3'>
                <AiOutlineSearch className='text-black opacity-60 text-2xl' />
                <input
                  type="text"
                  placeholder={t('search')}
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                  className='h-full flex-1 outline-none border-none w-full bg-transparent'
                />
              </div>
              <ul className='flex flex-wrap gap-[10px] mt-5 mb-2 text-[#000000DE]'>
                {visibleCategoryChips.length === 0 ? (
                  <li className='text-sm text-[#8F8F8F] py-2'>{t('No categories available')}</li>
                ) : (
                  visibleCategoryChips.map((chip) => (
                    <li
                      key={chip.id}
                      className={`px-3 w-fit h-8 flex items-center text-sm font-medium rounded cursor-pointer transition-colors duration-300 ${selectedItems.has(chip.id) ? 'bg-[#0b1223] text-white' : 'bg-[#f3f3f4] hover:bg-[#e0e0e0]'}`}
                      onClick={() => handleItemClick(chip.id)}
                    >
                      {selectedItems.has(chip.id) && <img src={TikmarkIcon} alt="" className='mr-2 w-6 h-6' />}
                      <span className='capitalize'>{chip.label}</span>
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}
        </div>

        <div className='px-4 pt-5 pb-7 flex items-center gap-4'>
          <button
            className='h-9 px-4 capitalize font-semibold rounded flex items-center justify-center text-sm transition-all duration-300 hover:bg-[#f3f3f4]'
            onClick={() => {
              setSelectedItems(new Set());
              setCategorySearch('');
            }}
          >
            {t('clearall')}
          </button>
          <button
            className='h-9 px-4 capitalize text-white font-semibold bg-[#0b1223] btnshadowOne rounded flex items-center justify-center text-sm transition-all duration-300 hover:opacity-80'
            onClick={() => {
              if (onApply) {
                onApply([...selectedItems]);
              }
              onClose();
            }}
          >
            {t('Apply Button')}
          </button>
        </div>
      </div>

      <div
        className={`fixed top-0 left-0 w-full h-dvh bg-[#212121] bg-opacity-[0.46] z-40 transition-opacity duration-300 ${
          show ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />
    </div>
  );
};

export default FilterModal;
